import React, { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { format, parseISO } from "date-fns";
import { CircularProgress, Dialog, IconButton, makeStyles } from "@material-ui/core";
import { ChevronLeft, ChevronRight, Close, GetApp, PlayArrow } from "@material-ui/icons";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { fileInfoFromUrl } from "./FileCard";

// Visualizador de fotos e vídeos da conversa, no padrão do WhatsApp Web: mídia no centro,
// anterior/próximo (setas, teclado e deslizar), contador, baixar e a fileira de miniaturas.

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const ink = isDark ? "#e9edef" : "#111b21";
  const soft = isDark ? "rgba(233,237,239,0.6)" : "#667781";
  const hover = isDark ? "rgba(255,255,255,0.1)" : "rgba(11,20,26,0.08)";
  return {
    paper: {
      display: "flex",
      flexDirection: "column",
      backgroundColor: isDark ? "#0b141a" : "#f7f8fa",
      backgroundImage: "none",
      color: ink,
      overflow: "hidden",
    },
    top: {
      flex: "none",
      display: "flex",
      alignItems: "center",
      gap: 4,
      minHeight: 60,
      padding: "8px 10px 8px 18px",
    },
    who: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      lineHeight: "19px",
    },
    whoName: {
      fontSize: 15,
      fontWeight: 500,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    whoDate: { fontSize: 12.5, color: soft },
    counter: {
      flex: "none",
      marginRight: 6,
      fontSize: 13,
      color: soft,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
    },
    iconBtn: {
      flex: "none",
      width: 40,
      height: 40,
      padding: 8,
      borderRadius: "50%",
      color: soft,
      "&:hover": { backgroundColor: hover },
    },
    stage: {
      flex: 1,
      minHeight: 0,
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "4px 64px",
      [theme.breakpoints.down("xs")]: { padding: "4px 0" },
    },
    media: {
      maxWidth: "100%",
      maxHeight: "100%",
      objectFit: "contain",
      borderRadius: 4,
      boxShadow: "0 2px 12px rgba(11,20,26,0.18)",
      backgroundColor: isDark ? "#111b21" : "#ffffff",
    },
    hidden: { visibility: "hidden", position: "absolute" },
    nav: {
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 2,
      width: 44,
      height: 44,
      padding: 8,
      borderRadius: "50%",
      color: ink,
      backgroundColor: isDark ? "rgba(32,44,51,0.9)" : "rgba(255,255,255,0.92)",
      boxShadow: "0 1px 3px rgba(11,20,26,0.25)",
      "&:hover": { backgroundColor: isDark ? "#2a3942" : "#ffffff" },
      "& svg": { fontSize: 28 },
    },
    navPrev: { left: 10 },
    navNext: { right: 10 },
    failed: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 10,
      padding: 24,
      color: soft,
      fontSize: 14,
      textAlign: "center",
    },
    link: {
      border: 0,
      background: "transparent",
      font: "inherit",
      fontWeight: 600,
      color: isDark ? "#53bdeb" : "#027eb5",
      cursor: "pointer",
    },
    caption: {
      flex: "none",
      maxHeight: 72,
      overflowY: "auto",
      padding: "8px 20px 2px",
      fontSize: 14,
      lineHeight: "20px",
      textAlign: "center",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    },
    strip: {
      flex: "none",
      display: "flex",
      justifyContent: "center",
      gap: 6,
      padding: "10px 12px 12px",
      overflow: "hidden",
    },
    thumb: {
      flex: "none",
      position: "relative",
      width: 52,
      height: 52,
      padding: 0,
      border: "3px solid transparent",
      borderRadius: 6,
      overflow: "hidden",
      cursor: "pointer",
      backgroundColor: isDark ? "#202c33" : "#d1d7db",
      opacity: 0.6,
      transition: "opacity 120ms ease",
      "&:hover": { opacity: 1 },
      "&:focus-visible": { outline: "2px solid #00a884", outlineOffset: 1 },
      "& img": { width: "100%", height: "100%", objectFit: "cover", display: "block" },
      [theme.breakpoints.down("xs")]: { width: 44, height: 44 },
    },
    thumbCurrent: {
      opacity: 1,
      borderColor: "#00a884",
    },
    thumbVideo: {
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#ffffff",
      backgroundColor: "rgba(11,20,26,0.45)",
    },
  };
});

// Imagem com as mesmas retentativas da bolha: falha momentânea (deploy, rede) tenta de novo
// com um parâmetro novo para fugir do erro em cache.
const GalleryImage = ({ url, alt, className, hiddenClassName, failedClassName, linkClassName, onDownload }) => {
  const [loaded, setLoaded] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  if (attempt > 3) {
    return (
      <div className={failedClassName}>
        {i18n.t("conversation.gallery.imageFailed")}
        <button type="button" className={linkClassName} onClick={onDownload}>
          {i18n.t("conversation.gallery.download")}
        </button>
      </div>
    );
  }
  const src = attempt ? `${url}${url.includes("?") ? "&" : "?"}r=${attempt}` : url;
  return (
    <>
      {!loaded && <CircularProgress color="inherit" size={34} />}
      <img
        className={clsx(className, !loaded && hiddenClassName)}
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        onError={() => {
          clearTimeout(timer.current);
          timer.current = setTimeout(() => setAttempt((n) => n + 1), 1200 * (attempt + 1));
        }}
      />
    </>
  );
};

const THUMBS_EACH_SIDE = 6;

const MediaGallery = ({ items, startId, onClose }) => {
  const classes = useStyles();
  const [currentId, setCurrentId] = useState(startId);
  const [videoFailed, setVideoFailed] = useState(false);
  const touchStart = useRef(null);

  const found = items.findIndex((item) => item.id === currentId);
  const index = found === -1 ? Math.max(0, items.findIndex((item) => item.id === startId)) : found;
  const item = items[index];

  const go = useCallback(
    (delta) => {
      const next = items[index + delta];
      if (next) {
        setVideoFailed(false);
        setCurrentId(next.id);
      }
    },
    [items, index]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        go(-1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        go(1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go]);

  // a mídia aberta sumiu da conversa (apagada) e não sobrou nenhuma: fecha
  useEffect(() => {
    if (!item) onClose();
  }, [item, onClose]);

  if (!item) return null;

  const { name } = fileInfoFromUrl(item.url, item.type === "video" ? "video.mp4" : "imagem.jpg");

  const download = async () => {
    try {
      const { data } = await api.get(item.url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
    } catch (e) {
      window.open(item.url, "_blank", "noopener,noreferrer");
    }
  };

  const first = Math.max(0, Math.min(index - THUMBS_EACH_SIDE, items.length - (THUMBS_EACH_SIDE * 2 + 1)));
  const thumbs = items.slice(first, first + THUMBS_EACH_SIDE * 2 + 1);

  return (
    <Dialog open fullScreen onClose={onClose} classes={{ paper: classes.paper }} aria-label={i18n.t("conversation.gallery.title")}>
      <div className={classes.top}>
        <div className={classes.who}>
          <span className={classes.whoName}>{item.author || i18n.t("conversation.gallery.title")}</span>
          <span className={classes.whoDate}>{format(parseISO(item.createdAt), "dd/MM/yyyy HH:mm")}</span>
        </div>
        <span className={classes.counter} aria-live="polite">
          {i18n.t("conversation.gallery.counter", { current: index + 1, total: items.length })}
        </span>
        <IconButton className={classes.iconBtn} onClick={download} aria-label={i18n.t("conversation.gallery.download")} title={i18n.t("conversation.gallery.download")}>
          <GetApp />
        </IconButton>
        <IconButton className={classes.iconBtn} onClick={onClose} aria-label={i18n.t("conversation.gallery.close")} title={i18n.t("conversation.gallery.close")}>
          <Close />
        </IconButton>
      </div>

      <div
        className={classes.stage}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
        onTouchStart={(e) => {
          touchStart.current = e.touches[0] ? e.touches[0].clientX : null;
        }}
        onTouchEnd={(e) => {
          const startX = touchStart.current;
          touchStart.current = null;
          if (startX === null || !e.changedTouches[0]) return;
          const dx = e.changedTouches[0].clientX - startX;
          if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1);
        }}
      >
        {index > 0 && (
          <IconButton className={clsx(classes.nav, classes.navPrev)} onClick={() => go(-1)} aria-label={i18n.t("conversation.gallery.previous")} title={i18n.t("conversation.gallery.previous")}>
            <ChevronLeft />
          </IconButton>
        )}
        {item.type === "image" ? (
          <GalleryImage
            key={item.id}
            url={item.url}
            alt={item.caption || name}
            className={classes.media}
            hiddenClassName={classes.hidden}
            failedClassName={classes.failed}
            linkClassName={classes.link}
            onDownload={download}
          />
        ) : videoFailed ? (
          <div className={classes.failed}>
            {i18n.t("conversation.gallery.videoFailed")}
            <button type="button" className={classes.link} onClick={download}>
              {i18n.t("conversation.gallery.download")}
            </button>
          </div>
        ) : (
          <video
            key={item.id}
            className={classes.media}
            src={item.url}
            controls
            autoPlay
            playsInline
            onError={() => setVideoFailed(true)}
          />
        )}
        {index < items.length - 1 && (
          <IconButton className={clsx(classes.nav, classes.navNext)} onClick={() => go(1)} aria-label={i18n.t("conversation.gallery.next")} title={i18n.t("conversation.gallery.next")}>
            <ChevronRight />
          </IconButton>
        )}
      </div>

      {item.caption ? <div className={classes.caption}>{item.caption}</div> : null}

      {items.length > 1 && (
        <div className={classes.strip}>
          {thumbs.map((thumb) => (
            <button
              key={thumb.id}
              type="button"
              className={clsx(classes.thumb, thumb.id === item.id && classes.thumbCurrent)}
              onClick={() => {
                setVideoFailed(false);
                setCurrentId(thumb.id);
              }}
              aria-label={i18n.t("conversation.gallery.open", { n: items.indexOf(thumb) + 1 })}
              aria-current={thumb.id === item.id}
            >
              {thumb.type === "image" ? (
                <img src={thumb.url} alt="" loading="lazy" />
              ) : (
                <span className={classes.thumbVideo}>
                  <PlayArrow />
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </Dialog>
  );
};

export default MediaGallery;
